/**
 * DatasheetListEditor — shape: datasheet_list (HOT_WATER_TANK_DATASHEETS).
 * The data is a Record<sku, HwtDatasheet> of ~115 commercial HWT SKUs. A
 * searchable list on the left; edit one SKU at a time on the right. Scalar
 * fields are text inputs; the list fields (connections / scopeOfSupply /
 * clientScope) are one-per-line textareas. Unknown keys round-trip untouched.
 */

import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { FieldLabel, SpecInput, MiniLabel, inputStyle } from './specPrimitives';

interface HwtDatasheet {
  sku: string;
  capacityL: number;
  material: string;
  pressureClass: string;
  shellMm: string | null;
  dishMm: string | null;
  insulation: string;
  outerCladding: string;
  internalFinish: string;
  connections: string[];
  scopeOfSupply: string[];
  clientScope: string[];
  warranty: string;
  hsn: string;
  gst: string;
}

const SCALAR_FIELDS: Array<{ key: keyof HwtDatasheet; label: string }> = [
  { key: 'capacityL', label: 'Capacity (L)' },
  { key: 'material', label: 'Material' },
  { key: 'pressureClass', label: 'Pressure Class' },
  { key: 'shellMm', label: 'Shell (mm)' },
  { key: 'dishMm', label: 'Dish (mm)' },
  { key: 'insulation', label: 'Insulation' },
  { key: 'outerCladding', label: 'Outer Cladding' },
  { key: 'internalFinish', label: 'Internal Finish' },
  { key: 'warranty', label: 'Warranty' },
  { key: 'hsn', label: 'HSN' },
  { key: 'gst', label: 'GST' },
];

const LIST_FIELDS: Array<{ key: keyof HwtDatasheet; label: string }> = [
  { key: 'connections', label: 'Connections' },
  { key: 'scopeOfSupply', label: 'Scope of Supply' },
  { key: 'clientScope', label: 'Client Scope' },
];

export function DatasheetListEditor({
  data,
  onChange,
}: {
  data: any;
  onChange: (next: any) => void;
}) {
  const sheets: Record<string, HwtDatasheet> =
    data && typeof data === 'object' && !Array.isArray(data) ? data : {};
  const skus = useMemo(() => Object.keys(sheets), [sheets]);

  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string>(skus[0] ?? '');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skus;
    return skus.filter((s) => s.toLowerCase().includes(q));
  }, [skus, query]);

  const current = sheets[selected];

  const patch = (k: keyof HwtDatasheet, v: any) =>
    onChange({ ...sheets, [selected]: { ...sheets[selected], [k]: v } });

  return (
    <FieldLabel label="Datasheets" hint={`${skus.length} SKUs — search and edit one at a time`}>
      <div style={{ display: 'flex', gap: 12 }}>
        {/* searchable SKU list */}
        <div style={{ width: 220, flexShrink: 0 }}>
          <div style={{ position: 'relative', marginBottom: 8 }}>
            <Search
              size={14}
              style={{ position: 'absolute', left: 9, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)' }}
            />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search SKU…"
              style={{ ...inputStyle, paddingLeft: 28 }}
            />
          </div>
          <div
            style={{
              border: '1px solid var(--line)',
              borderRadius: 9,
              background: 'var(--ivory-0)',
              maxHeight: 420,
              overflowY: 'auto',
              padding: 6,
            }}
          >
            {filtered.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--ink-3)', padding: 8 }}>No matching SKUs.</div>
            )}
            {filtered.map((sku) => {
              const on = sku === selected;
              return (
                <button
                  key={sku}
                  type="button"
                  onClick={() => setSelected(sku)}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '6px 9px',
                    marginBottom: 2,
                    borderRadius: 6,
                    fontSize: 11.5,
                    fontWeight: on ? 700 : 500,
                    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                    border: 'none',
                    cursor: 'pointer',
                    background: on ? 'var(--ink-1)' : 'transparent',
                    color: on ? 'var(--ivory-0)' : 'var(--ink-1)',
                    textAlign: 'left',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                  title={sku}
                >
                  {sku}
                </button>
              );
            })}
          </div>
        </div>

        {/* selected datasheet editor */}
        <div style={{ flex: 1, minWidth: 0 }}>
          {!current ? (
            <div style={{ fontSize: 12.5, color: 'var(--ink-3)', padding: 12 }}>
              {skus.length ? 'Select a SKU to edit.' : 'No datasheets in this family.'}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div
                style={{
                  fontSize: 13.5,
                  fontWeight: 700,
                  color: 'var(--ink-1)',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
                }}
              >
                {current.sku ?? selected}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                {SCALAR_FIELDS.map((f) => {
                  const raw = current[f.key];
                  const isNumber = f.key === 'capacityL';
                  return (
                    <div key={String(f.key)}>
                      <MiniLabel>{f.label}</MiniLabel>
                      {isNumber ? (
                        <input
                          type="number"
                          value={typeof raw === 'number' && Number.isFinite(raw) ? raw : ''}
                          onChange={(e) => patch(f.key, Number(e.target.value))}
                          style={inputStyle}
                        />
                      ) : (
                        <SpecInput
                          value={raw == null ? '' : String(raw)}
                          onChange={(v) => patch(f.key, v)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>

              {LIST_FIELDS.map((f) => {
                const arr = Array.isArray(current[f.key]) ? (current[f.key] as string[]) : [];
                return (
                  <div key={String(f.key)}>
                    <MiniLabel>{f.label} — one per line</MiniLabel>
                    <textarea
                      value={arr.join('\n')}
                      onChange={(e) =>
                        patch(
                          f.key,
                          e.target.value.split('\n').map((l) => l.replace(/\r$/, '')),
                        )
                      }
                      spellCheck={false}
                      style={{
                        ...inputStyle,
                        height: 'auto',
                        minHeight: 84,
                        padding: '8px 10px',
                        lineHeight: 1.5,
                        resize: 'vertical',
                      }}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </FieldLabel>
  );
}
